import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TrendingSidebar from '@/pages/home/components/TrendingSidebar';
import { categories, getArticlesByCategory, type Category } from '@/mocks/news';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category: Category | undefined = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20 px-4 md:px-6">
            <div className="text-center py-20">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
              <p className="text-foreground-600 mb-6">您访问的分类不存在或已被移除。</p>
              <Link to="/" className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
                返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 51黑料网`,
    description: `51黑料网${category.name}栏目，汇集最新最全的${category.name}资讯内容。`,
    url: `https://www.51heilia.com/category/${category.slug}`,
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: category.name },
            ]}
          />

          <main className="w-full px-4 md:px-6 pb-14">
            <div className="pt-6 pb-8">
              <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-2">
                {category.name}
              </h1>
              <p className="text-foreground-600 text-sm">
                共 {categoryArticles.length} 篇文章 · 51黑料网{classificationDescription(category.slug)}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 min-w-0">
                {categoryArticles.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                      <i className="ri-inbox-line text-3xl text-secondary-400"></i>
                    </div>
                    <p className="text-foreground-600">该分类暂无文章</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {categoryArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            title={`${article.title} - 51黑料网`}
                          />
                          {index === 0 && (
                            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                              最新
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-foreground-500">
                              {article.date}
                            </span>
                          </div>
                          <h2 className="font-heading text-base font-semibold text-foreground-950 line-clamp-2 leading-snug mb-2 group-hover:text-primary-500 transition-colors">
                            {article.title}
                          </h2>
                          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-100">
                            <span className="text-xs text-secondary-500">
                              <i className="ri-eye-line mr-1"></i>
                              {article.views.toLocaleString()}
                            </span>
                            <span className="text-xs text-secondary-500">
                              <i className="ri-chat-3-line mr-1"></i>
                              {article.comments.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <TrendingSidebar />
            </div>
          </main>
        </div>

        <Footer />
      </div>
    </>
  );
}

function classificationDescription(slug: string): string {
  const map: Record<string, string> = {
    latest: '最新爆料栏目',
    celebrity: '明星八卦栏目',
    influencer: '网红热点栏目',
    social: '社会事件栏目',
    investigation: '深度揭秘栏目',
  };
  return map[slug] || '';
}